import { Link } from "react-router-dom";
import { Activity, Bug, Landmark, ListChecks, ShieldCheck, Siren } from "lucide-react";
import { useFrameworks } from "@main/context/FrameworksContext";
import { useNavCountsContext } from "@main/nav/NavCountsContext";
import { DashboardPage } from "@main/pages/Dashboard";
import { card, type as text } from "@main/theme";

// ── Overview (Jersey edition) ─────────────────────────────────────────────
// Jersey shortcuts above the platform's own dashboard: the certification being
// worked towards, where the organisation stands under the Cyber Security
// (Jersey) Law 2026, reporting an incident to the JCSC, Cyber Essentials'
// 14-day security updates, and — for an organisation the law could designate —
// the continuity of its essential services. A member's own tasks show when any wait.

function Tile({ to, icon, title, body }: { to: string; icon: React.ReactNode; title: string; body: string }) {
  return (
    <Link to={to} style={{ ...card, padding: 16, textDecoration: "none", display: "flex", gap: 12, alignItems: "flex-start" }}>
      <span style={{ flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <span>
        <span style={{ ...text.itemTitle, display: "block" }}>{title}</span>
        <span style={{ ...text.hint, display: "block", marginTop: 3 }}>{body}</span>
      </span>
    </Link>
  );
}

export function JsyOverviewPage() {
  const { enrolled } = useFrameworks();
  const plus = enrolled.has("cyber_essentials_plus");
  const { counts } = useNavCountsContext();
  const client = counts?.role === "client" ? counts : null;
  const overdueUpdates = client?.vulnerabilities ?? 0;
  const myTasks = client?.myTasks ?? 0;
  return (
    <>
      <div style={{ padding: "20px 24px 0", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
        <Tile
          to={plus ? "/cyber-essentials-plus" : "/cyber-essentials"}
          icon={<ShieldCheck size={18} color="var(--c-accent)" />}
          title={plus ? "Cyber Essentials Plus" : "Cyber Essentials"}
          body="Work through the five controls and get ready for your assessment."
        />
        <Tile
          to="/legal"
          icon={<Landmark size={18} color="var(--c-accent)" />}
          title="Cyber Security (Jersey) Law 2026"
          body="Your obligations, and whether you could be an operator of essential services."
        />
        <Tile
          to="/resilience/incidents"
          icon={<Siren size={18} color="var(--c-danger)" />}
          title="Report an incident"
          body="Record it now; the platform tells you what the JCSC needs and by when."
        />
        <Tile
          to="/vulnerabilities"
          icon={<Bug size={18} color={overdueUpdates ? "var(--c-danger)" : "var(--c-accent)"} />}
          title="Security updates"
          body={overdueUpdates
            ? "Some vulnerabilities are past their deadline or known to be exploited. Cyber Essentials allows 14 days for critical and high updates."
            : "Every vulnerability on your devices with its 14-day Cyber Essentials deadline."}
        />
        {client?.oesPossible && (
          <Tile
            to="/resilience/bia"
            icon={<Activity size={18} color={(client.bia ?? 0) > 0 ? "var(--c-danger)" : "var(--c-accent)"} />}
            title="Essential service continuity"
            // oesPossible says only that the law's thresholds reach the sector; whether this
            // organisation is designated is the verdict on Your obligations (AUD-45).
            body="If the Cyber Security (Jersey) Law 2026 designates you (Your obligations says whether it does), record how long each essential service can be down and how it recovers."
          />
        )}
        {myTasks > 0 && (
          <Tile
            to="/my-tasks"
            icon={<ListChecks size={18} color="var(--c-accent)" />}
            title={`${myTasks} task${myTasks === 1 ? "" : "s"} waiting for you`}
            body="Policies to read and confirm, training to complete or approvals to decide."
          />
        )}
      </div>
      <DashboardPage />
    </>
  );
}
